import { User } from '../core/models';
import { AuthenticationService } from '../core/services/authentication/authentication.service';
import { GlobalEnvironmentService } from '../core/services/global-environment.service';
import { StateService } from '../core/services/state/state.service';
import { LanguageService } from '../services/language.service';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  EQimaAvatarSize,
  EQimaAvatarType,
  EQimaButtonIconStyle,
  EQimaButtonSize,
  EQimaDividerOrientation,
  EQimaIconName,
  QimaAvatarModule,
  QimaButtonIconModule,
  QimaDividerModule,
  QimaIconModule,
} from '@qima/ngx-qima';
import { filter } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, QimaButtonIconModule, QimaIconModule, QimaDividerModule, QimaAvatarModule, NgOptimizedImage],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  public readonly eQimaIconName = EQimaIconName;
  public readonly eQimaButtonSize = EQimaButtonSize;
  public readonly eQimaButtonIconStyle = EQimaButtonIconStyle;
  public readonly eQimaDividerOrientation = EQimaDividerOrientation;
  public readonly eQimaAvatarSize = EQimaAvatarSize;
  public readonly eQimaAvatarType = EQimaAvatarType;
  public isShowLanguageDropdown = false;
  public isMenuOpened: boolean = false;
  @ViewChild('menuContainer') public menuContainer!: ElementRef<HTMLDivElement>;

  public currentUser = this._stateService.currentUser;
  public selectedRole = this._stateService.selectedRole;
  public showHeader = this._stateService.showHeader;
  public readonly pageTitle = signal<string>('');

  public currentLanguageInfo = this._languageService.currentLanguageInfo;
  // Translation languages array for the dropdown (only languages with translation files)
  public languages = this._languageService.translationLanguages;

  public constructor(
    private readonly _router: Router,
    private readonly _languageService: LanguageService,
    private readonly _stateService: StateService,
    private readonly _globalEnvironmentService: GlobalEnvironmentService,
    private readonly _authService: AuthenticationService,
    private readonly _destroyRef: DestroyRef
  ) {
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((): void => {
        this.pageTitle.set(this._getActivatedRouteTitleKey() ?? '');
      });
  }

  public get currentLanguageFlag(): string {
    return this.currentLanguageInfo().flag;
  }

  public get currentLanguage(): string {
    return this._languageService.getCurrentLanguage();
  }

  public get currentLanguageUpperCase(): string {
    return this.currentLanguage.toUpperCase();
  }

  public get avatarUrl(): string {
    return '/assets/icons/avatar.svg';
  }

  public get canSwitchRole(): boolean {
    return (this.currentUser()?.roles.length ?? 0) > 1 && !this._router.url.startsWith('/login/role');
  }

  public get showHeaderValue(): boolean {
    return this.showHeader();
  }

  public get currentLanguageInfoValue(): { flag: string; name: string } {
    return this.currentLanguageInfo();
  }

  public onLogout(): void {
    this._authService.signOut(['/login']);
    this._clearSessionData();
  }

  public onSwitchRole(): void {
    this.isMenuOpened = false;
    void this._router.navigate(['/login/role'], {
      queryParams: { redirect: this._router.url },
    });
  }

  public onLanguageToggle(): void {
    this.isShowLanguageDropdown = !this.isShowLanguageDropdown;
  }

  public onWorkspaceClick(event: Event): void {
    event.stopPropagation();
    location.href = this._globalEnvironmentService.getWorkspaceUrl();
  }

  public onMenuClick(event: Event): void {
    event.stopPropagation();
    this.isMenuOpened = !this.isMenuOpened;
  }

  public onLogoClick(): void {
    void this._router.navigate(['/home']);
  }

  public onProfileClick(): void {
    // TODO: Implement profile click logic
  }

  public onLanguageChange(languageCode: string): void {
    this._languageService.setLanguage(languageCode);
    this.isShowLanguageDropdown = false; // Close dropdown after selection
  }

  @HostListener('document:click', ['$event'])
  public onMenuContainerClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target.closest('.language-dropdown')) {
      this.isShowLanguageDropdown = false;
    }

    // Check if the click is not on a button in the menu
    if (
      !target.closest('qima-button-icon') &&
      this.isMenuOpened &&
      this.menuContainer &&
      !this.menuContainer.nativeElement.contains(event.target as Node)
    ) {
      this.isMenuOpened = false;
    }
  }

  public getCurrentUser(): User | null {
    return this.currentUser() ?? null;
  }

  public getSelectedRole(): string | null {
    return this.selectedRole() ?? null;
  }

  private _clearSessionData(): void {
    // Local cleanup if needed
    sessionStorage.clear();
  }

  private _getActivatedRouteTitleKey(): string | null {
    let route = this._router.routerState.root;
    let key: string | null = null;

    while (route) {
      if (route.snapshot.data['pageTitleKey']) {
        key = route.snapshot.data['pageTitleKey'] as string;
      }

      route = route.firstChild!;
    }

    return key;
  }
}
